/**
 * Studying Now ribbon — UI Wave A.
 * Social-proof strip at top of dashboard: "🟢 142 studying right now" with
 * 5 avatar bubbles + tooltip on hover. Refreshes every 60s.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

function avatarBg(name) {
  // Deterministic pleasant background per first-name (no image needed if avatar_url empty)
  const palette = ["#3B82F6", "#F59E0B", "#10B981", "#EC4899", "#8B5CF6", "#EF4444", "#06B6D4"];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffff;
  return palette[h % palette.length];
}

export default function StudyingNowRibbon() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = () => api.get("/student/studying-now?limit=5")
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); });
    load();
    const id = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  if (!data || !data.total_active) return null;

  return (
    <div
      className="flex items-center gap-3 px-3 py-2 bg-emerald-50 border border-emerald-100 rounded-xl"
      data-testid="studying-now-ribbon"
    >
      <span className="relative flex h-2 w-2 shrink-0">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
      </span>
      <div className="flex -space-x-2 shrink-0">
        {(data.sample || []).slice(0, 5).map((s) => (
          <div
            key={s.id}
            title={`${s.first_name} is studying`}
            className="h-7 w-7 rounded-full border-2 border-emerald-50 grid place-items-center text-[11px] font-bold text-white overflow-hidden"
            style={{ background: avatarBg(s.first_name || "S") }}
            data-testid={`studying-avatar-${s.id}`}
          >
            {s.avatar_url ? (
              <img src={s.avatar_url} alt="" className="w-full h-full object-cover" loading="lazy" />
            ) : (
              (s.first_name || "S").slice(0, 1).toUpperCase()
            )}
          </div>
        ))}
      </div>
      <p className="text-xs text-emerald-900 leading-tight min-w-0 flex-1 truncate">
        <strong className="tabular-nums" data-testid="studying-total-count">{data.total_active}</strong>{" "}
        student{data.total_active !== 1 ? "s" : ""} studying right now
        {data.sample?.length > 0 && (
          <span className="hidden sm:inline text-emerald-700/80">
            {" · "}
            {data.sample.slice(0, 2).map((s) => s.first_name).join(", ")}
            {data.sample.length > 2 && ` +${data.total_active - 2} more`}
          </span>
        )}
      </p>
    </div>
  );
}
